/**
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} delay
 * @returns {T & { cancel(): void }}
 */
function debounce(fn, delay) {
  /** @type {number | undefined} */
  let timer;
  /**
   * @param {...any} args
   */
  function debounced(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  }
  debounced.cancel = () => {
    clearTimeout(timer);
  };
  return /** @type {T & { cancel(): void }} */ (debounced);
}

document.addEventListener("DOMContentLoaded", function () {
  // === Lazy-load videos ===
  const videoLazyObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const card = /** @type {HTMLElement} */ (entry.target);
        const video = /** @type {HTMLVideoElement | null} */ (
          card.querySelector("video.review-video")
        );
        if (!video) return;

        if (entry.isIntersecting) {
          // Only load if video has been played before (has data-loaded attribute)
          if (video.hasAttribute("data-loaded") && !video.src) {
            const realSrc = video.dataset.src;
            if (realSrc) {
              video.src = realSrc;
              video.load();
            }
          }
        } else {
          // Far away: pause & release buffer
          if (!video.paused) video.pause();
          if (video.src && !video.hasAttribute("data-keep-loaded")) {
            video.removeAttribute("src");
            video.load(); // drop the memory buffer
          }
        }
      });
    },
    { root: null, rootMargin: "300px" }
  );

  // Define the function globally before any templates use it
  /**
   * @param {string | null} dateString
   * @returns {string}
   */
  window.formatReviewDate = function (dateString) {
    if (!dateString) return "";

    const reviewDate = new Date(dateString);
    if (isNaN(reviewDate.getTime())) return dateString; // Return original if invalid

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    reviewDate.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - reviewDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day ago";
    if (diffDays <= 7) return `${diffDays} days ago`;

    // For dates older than 7 days, return in a readable format
    return reviewDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Fill all review dates without document.write
  document.querySelectorAll("[data-review-date]").forEach(function (el) {
    const rawDate = el.getAttribute("data-review-date");
    if (window.formatReviewDate) {
      el.textContent = window.formatReviewDate(rawDate);
    }
  });

  // Show more/less functionality
  function initShowMoreButtons() {
    document.querySelectorAll("[data-description]").forEach((descriptionEl) => {
      // Batch DOM reads to avoid forced reflow
      requestAnimationFrame(() => {
        // Only add button if content is truncated
        if (descriptionEl.scrollHeight > descriptionEl.clientHeight) {
          const btn = document.createElement("button");
          btn.className = "show-more-btn";
          btn.textContent = "Show more";
          btn.addEventListener("click", (e) => {
            e.preventDefault();
            descriptionEl.classList.toggle("expanded");
            btn.textContent = descriptionEl.classList.contains("expanded")
              ? "Show less"
              : "Show More";
          });

          // Append button after the text
          descriptionEl.insertAdjacentElement("afterend", btn);
        }
      });
    });
  }

  // Call the function after DOM is loaded
  initShowMoreButtons();

  const sections = document.querySelectorAll(".reviews-section");

  sections.forEach((section) => {
    const slider = /** @type {HTMLElement | null} */ (
      section.querySelector(".reviews-slider")
    );
    if (!slider) return;

    const cards = slider.querySelectorAll(".review-card:not(.clone)");

    if (!cards.length) return;

    let isInitialized = false;

    // Use Intersection Observer to only initialize when section comes into view
    const initObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isInitialized) {
            initializeSlider();
            isInitialized = true;
            initObserver.disconnect(); // Stop observing after initialization
          }
        });
      },
      {
        rootMargin: "50px", // Start initializing 50px before section is visible
      }
    );

    initObserver.observe(section);

    function initializeSlider() {
      // slider is guaranteed to be non-null here because we checked before
      const sliderElement = /** @type {HTMLElement} */ (slider);

      // Detect mobile device at slider initialization time
      const isMobileSlider = window.innerWidth <= 999;

      // Mark original cards for reference
      cards.forEach((card) => card.classList.add("original"));

      // Batch DOM reads to avoid forced reflows
      /** @type {number} */
      let cardWidth = 0;
      /** @type {number} */
      let originalContentWidth = 0;

      function updateCardDimensions() {
        const gap = parseInt(window.getComputedStyle(sliderElement).gap) || 15;
        const firstCard = /** @type {HTMLElement} */ (cards[0]);
        cardWidth = firstCard.offsetWidth + gap;
        originalContentWidth = cardWidth * cards.length;
      }

      // Initial dimension calculation
      updateCardDimensions();

      // Optimized cloning: Use minimal clones for better performance
      // Only 2 clones per side (4 total) instead of viewport-based calculation
      function createClones() {
        // Clear old clones
        sliderElement
          .querySelectorAll(".review-card.clone")
          .forEach((clone) => clone.remove());

        // Use fixed clone count for better performance
        const cloneCount = isMobileSlider ? 3 : 6;

        // Clone for right side (at the end)
        for (let i = 0; i < cloneCount; i++) {
          const card = cards[i];
          if (card) {
            const rightClone = /** @type {HTMLElement} */ (
              card.cloneNode(true)
            );
            rightClone.classList.add("clone", "right");
            // Mark clones to prevent video loading
            rightClone.setAttribute("data-clone", "true");
            sliderElement.appendChild(rightClone);
          }
        }

        // Clone for left side (at the beginning)
        for (let i = 0; i < cloneCount; i++) {
          const cardIndex = cards.length - 1 - i;
          const card = cards[cardIndex];
          if (card) {
            const leftClone = /** @type {HTMLElement} */ (card.cloneNode(true));
            leftClone.classList.add("clone", "left");
            // Mark clones to prevent video loading
            leftClone.setAttribute("data-clone", "true");
            sliderElement.prepend(leftClone);
          }
        }

        // Initialize videos in cloned cards
        setTimeout(initializeAllVideos, 50);
      }

      createClones();

      window.addEventListener(
        "resize",
        debounce(() => {
          updateCardDimensions();
          createClones();
        }, 300),
        {
          passive: true,
        }
      );

      // Initialize slider state
      const allCards = sliderElement.querySelectorAll(".review-card");
      let isDragging = false;
      let startPos = 0;
      let currentTranslate = 0;
      let prevTranslate = 0;
      /** @type {number | undefined} */
      let animationID;
      const autoScrollSpeed = 0.5;
      let isAutoScrolling = true;

      // Set initial position to show original content (centered)
      // Ensure originalContentWidth is initialized first
      updateCardDimensions();
      currentTranslate = -originalContentWidth;
      prevTranslate = currentTranslate;
      sliderElement.style.transform = `translateX(${currentTranslate}px)`;

      // Optimize slider with GPU acceleration
      if (isMobileSlider) {
        sliderElement.style.willChange = "transform";
      }

      // Event listeners - optimize for mobile
      if (isMobileSlider) {
        sliderElement.addEventListener("touchstart", touchStart, {
          passive: false,
        });
        sliderElement.addEventListener("touchmove", touchMove, {
          passive: false,
        });
        sliderElement.addEventListener("touchend", touchEnd, { passive: true });
      } else {
        sliderElement.addEventListener("touchstart", touchStart, {
          passive: false,
        });
        sliderElement.addEventListener("touchmove", touchMove, {
          passive: false,
        });
        sliderElement.addEventListener("touchend", touchEnd);
      }

      sliderElement.addEventListener("mousedown", pointerDown);
      sliderElement.addEventListener("mousemove", pointerMove);
      sliderElement.addEventListener("mouseup", pointerUp);
      sliderElement.addEventListener("mouseleave", pointerUp);

      // Video controls
      /** @type {HTMLVideoElement | null} */
      let currentlyPlaying = null;

      // Initialize all videos (original and clones)
      function initializeAllVideos() {
        // Initialize video click handlers
        initializeVideoHandlers();
      }

      // Initialize video click handlers
      function initializeVideoHandlers() {
        const videoWrappers = sliderElement.querySelectorAll(
          "[data-video-handle]"
        );

        videoWrappers.forEach((videoWrapper) => {
          // Skip if already has listener
          if (videoWrapper.hasAttribute("data-handler-added")) return;

          // Skip clones to improve performance
          const card = videoWrapper.closest(".review-card");
          if (card && card.hasAttribute("data-clone")) return;

          const wrapperEl = /** @type {HTMLElement} */ (videoWrapper);
          wrapperEl.setAttribute("data-handler-added", "true");
          const videoContainer = /** @type {HTMLElement | null} */ (
            wrapperEl.querySelector(".video-container")
          );
          const playOverlay = /** @type {HTMLElement | null} */ (
            wrapperEl.querySelector(".video-play-overlay")
          );
          const thumbnail = /** @type {HTMLElement | null} */ (
            wrapperEl.querySelector(".video-thumbnail")
          );

          if (!videoContainer) return;

          // Add click event to play overlay
          if (playOverlay) {
            playOverlay.addEventListener("click", function (e) {
              e.stopPropagation();
              toggleVideoPlayback(wrapperEl, videoContainer, thumbnail);
            });
          }

          // Add click event to video wrapper
          wrapperEl.addEventListener("click", function (e) {
            toggleVideoPlayback(wrapperEl, videoContainer, thumbnail);
          });
        });
      }

      // Function to toggle video playback
      /**
       * @param {HTMLElement} videoWrapper
       * @param {HTMLElement} videoContainer
       * @param {HTMLElement | null} thumbnail
       */
      function toggleVideoPlayback(videoWrapper, videoContainer, thumbnail) {
        if (!videoWrapper || !videoContainer) return;

        const videoUrl = videoWrapper.getAttribute("data-video-url");
        const playOverlay = videoWrapper.querySelector(".video-play-overlay");
        if (!videoUrl) return;

        // Check if video is already created
        let video = /** @type {HTMLVideoElement | null} */ (
          videoContainer.querySelector("video")
        );

        if (video) {
          // Video already exists, just toggle playback
          if (currentlyPlaying && currentlyPlaying !== video) {
            currentlyPlaying.pause();
            const playingWrapper = currentlyPlaying.closest(
              "[data-video-handle]"
            );
            if (playingWrapper) {
              /** @type {HTMLElement} */ (playingWrapper).classList.remove(
                "playing"
              );
            }
          }

          if (video.paused) {
            const videoEl = video; // Capture reference for closure
            video
              .play()
              .then(() => {
                if (videoEl) {
                  videoEl.muted = false;
                  videoWrapper.classList.add("playing");
                  currentlyPlaying = videoEl;
                  // Hide thumbnail if it's still visible
                  if (thumbnail && thumbnail.style.display !== "none") {
                    thumbnail.style.display = "none";
                  }
                }
              })
              .catch((/** @type {Error} */ err) =>
                console.error("Video play failed:", err)
              );
          } else {
            video.pause();
            videoWrapper.classList.remove("playing");
            currentlyPlaying = null;
          }
        } else {
          // Create video element for the first time
          if (currentlyPlaying) {
            currentlyPlaying.pause();
            const playingWrapper = currentlyPlaying.closest(
              "[data-video-handle]"
            );
            if (playingWrapper) {
              /** @type {HTMLElement} */ (playingWrapper).classList.remove(
                "playing"
              );
            }
            if (playOverlay) {
              /** @type {HTMLElement} */ (playOverlay).style.display = "flex";
            }
          }

          // Show loading indicator
          const loader = createVideoLoader();
          videoContainer.appendChild(loader);
          if (playOverlay) {
            /** @type {HTMLElement} */ (playOverlay).style.display = "none";
          }

          video = document.createElement("video");
          video.className = "review-video";
          video.setAttribute("playsinline", "");
          video.setAttribute("data-loaded", "true");
          video.setAttribute("data-keep-loaded", "true");

          const source = document.createElement("source");
          source.src = videoUrl;
          source.type = "video/mp4";
          video.appendChild(source);

          // Function to hide loader and show video
          const hideLoaderAndShowVideo = () => {
            if (loader && loader.parentNode) {
              loader.remove();
            }

            if (video) {
              video
                .play()
                .then(() => {
                  if (video) {
                    video.muted = false;
                    videoWrapper.classList.add("playing");
                    currentlyPlaying = video;

                    // Hide thumbnail
                    if (thumbnail) {
                      thumbnail.style.display = "none";
                    }
                  }
                })
                .catch((/** @type {Error} */ err) =>
                  console.error("Video play failed:", err)
                );
            }
          };

          // Handle loading errors
          const onError = () => {
            if (loader && loader.parentNode) {
              loader.remove();
            }
            console.error("Failed to load video:", videoUrl);
          };

          video.addEventListener("canplay", hideLoaderAndShowVideo, {
            once: true,
          });
          video.addEventListener("loadeddata", hideLoaderAndShowVideo, {
            once: true,
          });
          video.addEventListener("error", onError, { once: true });

          videoContainer.appendChild(video);
          video.load();
        }
      }

      // Function to create video loader
      function createVideoLoader() {
        const loader = document.createElement("div");
        loader.className = "video-loading-indicator";
        loader.innerHTML = `
          <div class="video-loader-spinner"></div>
        `;
        return loader;
      }

      // Initialize videos after a short delay
      setTimeout(() => {
        initializeAllVideos();
        initializeVideoHandlers();
      }, 100);

      // Helper functions
      /**
       * @param {MouseEvent | TouchEvent} event
       * @returns {number}
       */
      function getPositionX(event) {
        if (event.type.includes("mouse")) {
          return /** @type {MouseEvent} */ (event).pageX;
        } else {
          const touchEvent = /** @type {TouchEvent} */ (event);
          return touchEvent.touches && touchEvent.touches[0]
            ? touchEvent.touches[0].clientX
            : 0;
        }
      }

      /**
       * @param {MouseEvent} e
       */
      function pointerDown(e) {
        if (e.button !== 0) return;
        startDrag(e);
      }

      /**
       * @param {TouchEvent} e
       */
      function touchStart(e) {
        startDrag(e);
      }

      /**
       * @param {MouseEvent | TouchEvent} e
       */
      function startDrag(e) {
        isDragging = true;
        startPos = getPositionX(e);
        if (animationID) {
          cancelAnimationFrame(animationID);
        }
        isAutoScrolling = false;
        sliderElement.style.cursor = "grabbing";
        sliderElement.classList.add("no-transition");
      }

      /**
       * @param {MouseEvent} e
       */
      function pointerMove(e) {
        if (!isDragging) return;
        handleDrag(e);
      }

      /**
       * @param {TouchEvent} e
       */
      function touchMove(e) {
        if (!isDragging) return;
        handleDrag(e);
      }

      /**
       * @param {MouseEvent | TouchEvent} e
       */
      function handleDrag(e) {
        const currentPos = getPositionX(e);
        currentTranslate = prevTranslate + currentPos - startPos;
        sliderElement.style.transform = `translate3d(${currentTranslate}px,0,0)`;
      }

      function pointerUp() {
        endDrag();
      }

      function touchEnd() {
        endDrag();
      }

      function endDrag() {
        if (!isDragging) return;
        isDragging = false;
        sliderElement.style.cursor = "grab";
        sliderElement.classList.remove("no-transition");

        checkForReset();
        startAutoScroll();
      }

      // Optimized reset function - smoother for infinite scroll
      function checkForReset() {
        const boundary = originalContentWidth;

        // Optimized boundary checking - use simple modulo reset
        if (currentTranslate > -boundary * 0.5) {
          currentTranslate -= boundary;
          sliderElement.style.transform = `translate3d(${currentTranslate}px,0,0)`;
        } else if (currentTranslate <= -boundary * 1.5) {
          currentTranslate += boundary;
          sliderElement.style.transform = `translate3d(${currentTranslate}px,0,0)`;
        }

        prevTranslate = currentTranslate;
      }

      function startAutoScroll() {
        isAutoScrolling = true;
        let last = performance.now();

        /**
         * @param {number} now
         */
        const step = (now) => {
          if (!isAutoScrolling) return;

          const dt = now - last;
          last = now;

          if (!isDragging) {
            currentTranslate += autoScrollSpeed * (dt / 16); // normalise to 60 fps
            prevTranslate = currentTranslate;

            sliderElement.style.transform = `translate3d(${currentTranslate}px,0,0)`;

            checkForReset();
          }
          animationID = requestAnimationFrame(step);
        };

        animationID = requestAnimationFrame(step);
      }

      // Pause videos when they go out of view
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector("video");
            const wrapper = entry.target.querySelector("[data-video-handle]");
            const thumbnail = entry.target.querySelector(".video-thumbnail");

            if (!video || !wrapper) return;

            if (!entry.isIntersecting && !video.paused) {
              video.pause();
              wrapper.classList.remove("playing");

              // Show thumbnail again if video hasn't been loaded yet
              if (!video.hasAttribute("data-loaded") && thumbnail) {
                /** @type {HTMLElement} */ (thumbnail).style.display = "block";
              }

              if (currentlyPlaying === video) {
                currentlyPlaying = null;
              }
            }
          });
        },
        { threshold: 0.5 }
      );

      allCards.forEach((card) => {
        observer.observe(card);
        videoLazyObserver.observe(card);
      });

      startAutoScroll();
    }
  });
});
